<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { logout, updatePassword } from '@ck/auth'
import { accountsApi } from '@ck/api-client'
import { CkAlert, CkButton, CkField } from '@ck/ui'

const account = useQuery({ queryKey: ['accounts', 'me'], queryFn: () => accountsApi.me() })

const form = reactive({ name: '', phone: '' })
const errors = ref<{ name?: string[]; phone?: string[] }>({})
const saved = ref(false)

watch(() => account.data.value, (data) => {
  if (!data) return
  form.name = data.name ?? ''
  form.phone = data.phone ?? ''
}, { immediate: true })

const mutation = useMutation({
  mutationFn: () => accountsApi.updateMe({ name: form.name.trim(), phone: form.phone.trim() || null }),
  onSuccess: () => { saved.value = true },
  onError: () => { saved.value = false }
})

function submit() {
  saved.value = false
  errors.value = {}
  if (form.name.trim().length < 3) errors.value.name = ['Nome deve ter ao menos 3 caracteres.']
  if (Object.keys(errors.value).length > 0) return
  mutation.mutate()
}
</script>

<template>
  <div>
    <div class="page-head"><h1>Minha conta</h1><p>Dados de acesso e segurança</p></div>

    <section class="panel">
      <h2>Perfil</h2>
      <CkAlert v-if="saved" tone="success">Dados atualizados.</CkAlert>
      <CkAlert v-else-if="mutation.isError.value" tone="error">Não foi possível salvar. Tente novamente.</CkAlert>

      <form class="account-form" @submit.prevent="submit">
        <CkField
          v-model="form.name" label="Nome completo" autocomplete="name" required
          :error="errors.name?.[0]" :disabled="mutation.isPending.value"
        />
        <CkField label="E-mail" :model-value="account.data.value?.email ?? ''" type="email" disabled hint="O e-mail não pode ser alterado por aqui." />
        <CkField label="Papéis" :model-value="(account.data.value?.roles ?? []).join(', ')" disabled />
        <CkButton type="submit" :disabled="mutation.isPending.value">
          {{ mutation.isPending.value ? 'Salvando…' : 'Salvar alterações' }}
        </CkButton>
      </form>
    </section>

    <section class="panel">
      <h2>Segurança</h2>
      <p class="hint">A troca de senha acontece na página segura do provedor de identidade.</p>
      <div class="actions">
        <CkButton variant="secondary" @click="updatePassword">Alterar senha</CkButton>
        <CkButton variant="ghost" @click="logout">Sair</CkButton>
      </div>
    </section>
  </div>
</template>

<style scoped>
.page-head { margin-bottom: 20px; }
.page-head h1 { margin: 0; font-family: var(--font-display); font-size: 26px; font-weight: 800; }
.page-head p { margin: 4px 0 0; color: var(--color-text-faint); font-size: 13px; }
.panel { margin-bottom: 20px; padding: 20px; border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.panel h2 { margin: 0 0 16px; font-size: 18px; }
.account-form { display: grid; gap: 4px; max-width: 420px; }
.hint { color: var(--color-text-muted); font-size: 13px; margin: 0 0 14px; }
.actions { display: flex; gap: 10px; flex-wrap: wrap; }
</style>
